import { useEffect, useState } from "react";
import {
  obtenerResumenDashboard,
  obtenerFacturacionMensual,
} from "../services/dashboardService";
import FacturacionMensualChart from "../components/FacturacionMensualChart";

const StatCard = ({ label, value, icon, variant }) => (
  <div className={`lmd-stat-card${variant ? ` ${variant}` : ""}`}>
    <div className="lmd-stat-card-top">
      <span className="lmd-stat-label">{label}</span>
      <div className={`lmd-stat-icon lmd-stat-icon-${icon}`}>
        {icon === "blue" && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
        )}
        {icon === "yellow" && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        )}
        {icon === "green" && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
        )}
        {icon === "white" && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
        )}
      </div>
    </div>
    <div className="lmd-stat-value">{value}</div>
  </div>
);

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export default function DashboardPage() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [anios, setAnios] = useState([]);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const resumen = await obtenerResumenDashboard(anio, mes);
        if (!cancel) setStats(resumen);
        const facturacion = await obtenerFacturacionMensual(anio);
        if (!cancel) {
          setChartData(facturacion?.data ?? []);
          setAnios(facturacion?.anios ?? []);
        }
      } catch (err) {
        if (!cancel) setError(err.message || "Error al cargar dashboard");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [anio, mes]);

  const user = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem("user") || "null");
      // Login.js guarda la respuesta completa del login: { token, user: {...} }
      return raw?.user ?? raw ?? null;
    } catch { return null; }
  })();
  const userName = user?.nombre ?? user?.Nombre ?? user?.clave ?? "Usuario";

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Header */}
      <div className="lmd-page-header">
        <div>
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)", margin: "0 0 4px" }}>
            Bienvenido, <strong style={{ color: "var(--clr-text-primary)" }}>{userName}</strong>
          </p>
          <h1 className="lmd-page-title">Dashboard</h1>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)", marginBottom: 6 }}>
            {new Date().toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <select
              className="lmd-select"
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
            >
              <option value={0}>Todos</option>
              {MESES.map((nombre, i) => (
                <option key={i + 1} value={i + 1}>{nombre}</option>
              ))}
            </select>
            <select
              className="lmd-select"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
            >
              {(anios.length ? anios : [anio]).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 10, color: "var(--clr-text-muted)" }}>
          <div className="lmd-spinner" />
          Cargando dashboard...
        </div>
      )}

      {!loading && error && (
        <div style={{ background: "var(--clr-danger-bg)", color: "var(--clr-danger)", padding: "16px 20px", borderRadius: "var(--radius-md)", border: "1px solid #fecaca" }}>
          {error}
        </div>
      )}

      {!loading && !error && (() => {
        const esTodoElAnio = mes === 0;
        const periodoLabel = esTodoElAnio ? `${anio}` : `${MESES[mes - 1]} ${anio}`;
        const palabraPeriodo = esTodoElAnio ? "año" : "mes";
        return (
        <>
      {/* Stats */}
      <div className="lmd-stats-grid">
        <StatCard
          label={`Cotizaciones del ${palabraPeriodo}`}
          value={stats?.cotizacionesMes ?? 0}
          icon="blue"
        />
        <StatCard
          label="Por facturar"
          value={stats?.cotizacionesPorFacturar ?? 0}
          icon="yellow"
        />
        <StatCard
          label={`Facturas del ${palabraPeriodo}`}
          value={stats?.facturasMes ?? 0}
          icon="green"
        />
        <StatCard
          label="Total facturado"
          value={`$${Number(stats?.totalFacturado ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          icon="white"
          variant="accent"
        />
      </div>

      {/* Conversion de cotizaciones */}
      {(() => {
        const cerradas = stats?.cotizacionesCerradasMes ?? 0;
        const sinCerrar = stats?.cotizacionesSinCerrarMes ?? 0;
        const totalCot = stats?.cotizacionesMes ?? 0;
        const pct = totalCot > 0 ? Math.round((cerradas / totalCot) * 100) : 0;
        return (
          <div className="lmd-card" style={{ marginTop: "var(--sp-6)" }}>
            <div className="lmd-card-header">
              <h2 className="lmd-card-title">Conversion de cotizaciones — {periodoLabel}</h2>
              <span style={{ fontSize: "var(--fs-xs)", color: "var(--clr-text-muted)" }}>
                Cotizaciones que se convirtieron en factura
              </span>
            </div>
            <div className="lmd-card-body">
              <div style={{ display: "flex", gap: "var(--sp-6)", flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>Facturadas</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "var(--clr-success)" }}>{cerradas}</div>
                </div>
                <div>
                  <div style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>Sin facturar</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "var(--clr-warning)" }}>{sinCerrar}</div>
                </div>
                <div>
                  <div style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>Total del {palabraPeriodo}</div>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>{totalCot}</div>
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--fs-sm)", marginBottom: 6 }}>
                    <span style={{ color: "var(--clr-text-muted)" }}>Tasa de conversion</span>
                    <strong>{pct}%</strong>
                  </div>
                  <div style={{ height: 10, borderRadius: 999, background: "var(--clr-warning-bg)", overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: "var(--clr-success)", transition: "width .4s ease" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Chart */}
      <div className="lmd-card" style={{ marginTop: "var(--sp-6)" }}>
        <div className="lmd-card-header">
          <h2 className="lmd-card-title">Facturacion — {anio}</h2>
          <span style={{ fontSize: "var(--fs-xs)", color: "var(--clr-text-muted)" }}>
            Actualizado hoy
          </span>
        </div>
        <div className="lmd-card-body">
          <FacturacionMensualChart data={chartData} />
        </div>
      </div>
        </>
        );
      })()}
    </div>
  );
}
